// src/pages/CadastrarIncorporadora.jsx
import React, { useState } from 'react';
import { db, storage, serverTimestamp } from '../services/firebase';
import { collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { IMaskInput } from 'react-imask';

// Componente Card para reutilização, sem alterações.
const Card = ({ title, children }) => (
  <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 mb-6">
    <h2 className="text-xl font-semibold text-gray-800 mb-4">{title}</h2>
    {children}
  </div>
);

const capitalizeWords = (text = '') =>
  String(text)
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');

const CadastrarIncorporadora = () => {
  const navigate = useNavigate();
  const { usuario, loadingUserDoc } = useAuth();

  const [dados, setDados] = useState({
    nome: '',
    descricao: '',
    cnpj: '',
    cidade: '',
    estado: '',
    site: '',
  });

  const [imagem, setImagem] = useState(null);
  const [previewImagem, setPreviewImagem] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setDados((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleImagemChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImagem(file);
      setPreviewImagem(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let urlImagem = '';
      if (imagem) {
        const caminho = `incorporadoras/${usuario.uid}/${Date.now()}_${imagem.name}`;
        const storageRef = ref(storage, caminho);
        await uploadBytes(storageRef, imagem);
        urlImagem = await getDownloadURL(storageRef);
      }

      await addDoc(collection(db, 'incorporadoras'), {
        ...dados,
        // ✅ normaliza antes de salvar
        nome: capitalizeWords(dados.nome),
        cidade: capitalizeWords(dados.cidade),
        estado: String(dados.estado || '')
          .trim()
          .toUpperCase(),
        // cnpj vem unmask só dígitos
        cnpj: String(dados.cnpj || '').trim(),
        criadoEm: serverTimestamp(),
        usuarioId: usuario.uid,
        imagem: urlImagem,
      });

      toast.success('Incorporadora cadastrada com sucesso!');
      navigate('/perfil');
    } catch (error) {
      console.error('Erro ao cadastrar:', error);
      toast.error('Erro ao cadastrar incorporadora.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingUserDoc || !usuario) {
    return (
      <p className="text-center text-gray-500">
        Carregando dados do usuário...
      </p>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 p-4 rounded-lg leading-relaxed">
        <p>
          Utilize este formulário para cadastrar incorporadoras, construtoras e
          urbanizadoras com as quais você trabalha <strong>diretamente</strong>.
          Através disso, outros profissionais poderão lhe procurar para{' '}
          <strong>sociedade</strong> ou <strong>parcerias</strong>. Essas
          informações serão exibidas no seu perfil público como forma de{' '}
          <strong>validação</strong> e <strong>autoridade</strong>.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card title="Dados da Incorporadora">
          <div className="grid grid-cols-1 gap-4">
            <input
              type="text"
              name="nome"
              value={dados.nome}
              onChange={handleChange}
              onBlur={() =>
                setDados((p) => ({ ...p, nome: capitalizeWords(p.nome) }))
              }
              placeholder="Nome da Incorporadora"
              required
              className="input"
            />

            <textarea
              name="descricao"
              value={dados.descricao}
              onChange={handleChange}
              placeholder="Descrição (opcional)"
              className="input"
            />

            {/* ✅ CNPJ com máscara e salvando só dígitos */}
            <IMaskInput
              mask="00.000.000/0000-00"
              unmask={true} // salva "12345678000199"
              name="cnpj"
              value={dados.cnpj}
              onAccept={(v) => setDados((p) => ({ ...p, cnpj: v }))}
              placeholder="CNPJ"
              required
              className="input"
              inputMode="numeric"
            />

            <input
              type="text"
              name="cidade"
              value={dados.cidade}
              onChange={handleChange}
              onBlur={() =>
                setDados((p) => ({ ...p, cidade: capitalizeWords(p.cidade) }))
              }
              placeholder="Cidade"
              className="input"
            />

            {/* ✅ Estado: 2 letras maiúsculas */}
            <IMaskInput
              mask="aa"
              prepare={(str) => String(str || '').toUpperCase()}
              name="estado"
              value={dados.estado}
              onAccept={(v) => setDados((p) => ({ ...p, estado: v }))}
              placeholder="UF (ex: MT)"
              className="input"
            />

            <input
              type="text"
              name="site"
              value={dados.site}
              onChange={handleChange}
              placeholder="Site institucional (opcional)"
              className="input"
            />
          </div>
        </Card>

        <Card title="Imagem da Incorporadora (opcional)">
          <input
            type="file"
            accept="image/*"
            onChange={handleImagemChange}
            className="w-full p-2 border rounded-lg"
          />
          {previewImagem && (
            <img
              src={previewImagem}
              alt="Pré-visualização da Imagem"
              className="mt-4 max-h-48 rounded-lg border object-cover w-full"
            />
          )}
        </Card>

        <button
          type="submit"
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition w-full"
          disabled={loading}
        >
          {loading ? 'Salvando...' : 'Cadastrar Incorporadora'}
        </button>
      </form>

      <style jsx>{`
        .input {
          @apply block w-full p-3 border border-gray-300 rounded-lg text-base text-gray-900 focus:ring-blue-500 focus:border-blue-500;
        }
      `}</style>
    </div>
  );
};

export default CadastrarIncorporadora;
